import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ticketsService } from './service';
import type { TicketDetail, TicketListQuery, TicketPriority, TicketStatus } from './types';

/** Local to the feature, like its endpoints. */
export const ticketKeys = {
  all: ['tickets'] as const,
  list: (query: TicketListQuery) => [...ticketKeys.all, 'list', query] as const,
  counts: () => [...ticketKeys.all, 'counts'] as const,
  detail: (id: string) => [...ticketKeys.all, 'detail', id] as const,
};

export function useTickets(query: TicketListQuery) {
  return useQuery({
    queryKey: ticketKeys.list(query),
    queryFn: ({ signal }) => ticketsService.list(query, signal),
    placeholderData: keepPreviousData,
    staleTime: 30 * 1000,
  });
}

export function useTicketCounts() {
  return useQuery({
    queryKey: ticketKeys.counts(),
    queryFn: ({ signal }) => ticketsService.counts(signal),
    staleTime: 30 * 1000,
  });
}

export function useTicket(id: string) {
  return useQuery({
    queryKey: ticketKeys.detail(id),
    queryFn: ({ signal }) => ticketsService.get(id, signal),
  });
}

/** The detail comes back from every write; the lists and tiles refetch. */
function useSettle() {
  const queryClient = useQueryClient();
  return (ticket: TicketDetail) => {
    queryClient.setQueryData(ticketKeys.detail(ticket.id), ticket);
    void queryClient.invalidateQueries({ queryKey: [...ticketKeys.all, 'list'] });
    void queryClient.invalidateQueries({ queryKey: ticketKeys.counts() });
  };
}

export function useReplyToTicket(id: string) {
  const settle = useSettle();
  return useMutation({
    mutationFn: (body: { body: string; status?: TicketStatus }) => ticketsService.reply(id, body),
    onSuccess: (ticket) => {
      settle(ticket);
      toast.success(ticket.raisedBy ? `Reply sent to ${ticket.raisedBy.email}.` : 'Reply saved.');
    },
  });
}

export function useUpdateTicket(id: string) {
  const settle = useSettle();
  return useMutation({
    mutationFn: (body: { status?: TicketStatus; priority?: TicketPriority }) =>
      ticketsService.update(id, body),
    onSuccess: (ticket, body) => {
      settle(ticket);
      if (body.status === 'resolved' || body.status === 'closed') {
        toast.success(`Ticket ${ticket.ref} ${body.status}. The lab has been emailed.`);
      }
    },
  });
}
